import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, BadgeCheck } from 'lucide-react';

/**
 * The permanent CPDForge trust stack, surfaced consistently across public
 * commercial + trust surfaces:
 *   CPDForge  — creates & delivers learning
 *   CPFPSI    — standards governance, QA & credential framework
 *   Core Compliant — independent accreditation & verification body
 * These three roles are distinct and must never be conflated.
 */
const CC_ICON = '/brand/core-compliant-icon.png';
const CC_ORANGE = '#FF7A00';

/* Compact horizontal strip for high-intent commercial surfaces (home, programme, credential). */
export function TrustStackStrip({ className = '', dataTestId = 'trust-stack-strip' }) {
  const items = [
    { key: 'cpdforge', node: (<span className="font-extrabold text-slate-900 dark:text-white">CPDForge</span>), sub: 'Creates & delivers' },
    { key: 'cpfpsi', node: (<span className="font-extrabold text-indigo-600 dark:text-indigo-400">CPFPSI</span>), sub: 'Governs quality' },
    { key: 'core-compliant', node: (<span className="font-extrabold" style={{ color: CC_ORANGE }}>Core Compliant</span>), sub: 'Accredits & verifies' },
  ];
  return (
    <div
      data-testid={dataTestId}
      className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 px-4 py-3 ${className}`}
    >
      {items.map((it, i) => (
        <React.Fragment key={it.key}>
          <div className="flex items-center gap-2" data-testid={`trust-stack-${it.key}`}>
            {it.key === 'core-compliant'
              ? <img src={CC_ICON} alt="Core Compliant" className="w-5 h-5 object-contain" />
              : <ShieldCheck className={`w-4 h-4 ${it.key === 'cpfpsi' ? 'text-indigo-500' : 'text-slate-400'}`} />}
            <span className="text-sm leading-none">
              {it.node}
              <span className="block text-[10px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500 mt-0.5">{it.sub}</span>
            </span>
          </div>
          {i < items.length - 1 && <span className="text-slate-300 dark:text-slate-600 text-lg leading-none" aria-hidden>→</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

/* Small "Core Compliant Accredited" pill for cards / rows. */
export function CoreCompliantBadge({ className = '', label = 'Core Compliant Accredited', size = 'md', dataTestId = 'core-compliant-badge' }) {
  const sizeCls = size === 'sm' ? 'gap-1 px-2 py-0.5 text-[10px]' : 'gap-1.5 px-3 py-1 text-xs';
  const iconCls = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  return (
    <span
      data-testid={dataTestId}
      title="Accredited by Core Compliant — an independent accreditation & verification body"
      className={`inline-flex items-center rounded-full border font-bold ${sizeCls} ${className}`}
      style={{ borderColor: '#FF7A0055', color: CC_ORANGE, background: '#FF7A000f' }}
    >
      <img src={CC_ICON} alt="" className={`${iconCls} object-contain`} /> {label}
    </span>
  );
}

/* "Why trust this credential" panel for public programme / credential detail pages. */
export function WhyTrustThisCredentialPanel({ className = '', accreditedLabel = 'Core Compliant Accredited Programme' }) {
  const rows = [
    { icon: ShieldCheck, color: 'text-indigo-500', title: 'CPFPSI governed', desc: 'Quality-assured against a 14-criterion framework with human SME sign-off.' },
    { icon: BadgeCheck, color: 'text-emerald-500', title: 'Standards grounded', desc: 'Built on authoritative source material — fail-loud grounding, no fabricated content.' },
    { icon: ShieldCheck, color: 'text-sky-500', title: 'Publicly verifiable', desc: 'Every credential has a public verification record and QR code.' },
  ];
  return (
    <div
      data-testid="why-trust-credential-panel"
      className={`rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-6 ${className}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="text-base font-bold text-slate-900 dark:text-white">Why you can trust this credential</h3>
        <CoreCompliantBadge label={accreditedLabel} />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-col gap-1.5" data-testid={`why-trust-row-${i}`}>
            <r.icon className={`w-5 h-5 ${r.color}`} />
            <p className="text-sm font-bold text-slate-900 dark:text-white">{r.title}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{r.desc}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <Link to="/professional-standards-institute" className="font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">About CPFPSI &rarr;</Link>
        <Link to="/verify/sample" className="font-semibold text-slate-600 dark:text-slate-300 hover:underline" data-testid="why-trust-verify-link">See a verified credential &rarr;</Link>
      </div>
    </div>
  );
}

/* Contextual internal-link block connecting SEO spoke pages to the trust/accreditation hubs.
   Completes the hub-and-spoke internal-linking architecture. */
export function TrustHubLinks({ className = '', heading = 'Accreditation, verification & governance' }) {
  const links = [
    { to: '/accreditation', label: 'Core Compliant Accreditation', desc: 'What accreditation means' },
    { to: '/verification', label: 'How Verification Works', desc: 'Confirm any credential' },
    { to: '/accreditation/registry', label: 'Accreditation Registry', desc: 'Accredited programmes' },
    { to: '/credentials', label: 'Credential Pathways', desc: 'Credential progression' },
    { to: '/professional-standards-institute', label: 'CPFPSI Framework', desc: 'Quality governance' },
    { to: '/trust', label: 'Trust Centre', desc: 'Security & procurement' },
  ];
  return (
    <div data-testid="trust-hub-links" className={`rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 p-6 ${className}`}>
      <div className="flex items-center gap-2 mb-1">
        <img src={CC_ICON} alt="" className="w-5 h-5 object-contain" />
        <h3 className="text-base font-bold text-slate-900 dark:text-white">{heading}</h3>
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Every CPDForge programme is Core Compliant Accredited, CPFPSI-governed and publicly verifiable.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {links.map((l) => (
          <Link key={l.to} to={l.to} data-testid={`trust-hub-link-${l.to.replace(/\//g, '-').replace(/^-/, '')}`}
            className="group flex items-center gap-2.5 p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors">
            <ShieldCheck className="w-4 h-4 text-indigo-500 flex-shrink-0" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-slate-900 dark:text-white truncate group-hover:text-indigo-600">{l.label}</span>
              <span className="block text-[11px] text-slate-400 dark:text-slate-500 truncate">{l.desc}</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
export function TrustStackFooterBlock({ className = '' }) {
  const layers = [
    { name: 'CPDForge', role: 'Creates, delivers & manages learning', to: '/about' },
    { name: 'CPFPSI', role: 'Standards governance, QA & credential framework', to: '/professional-standards-institute' },
    { name: 'Core Compliant', role: 'Independent accreditation & verification', to: '/verify/sample', orange: true },
  ];
  return (
    <div data-testid="footer-trust-stack" className={`rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 p-5 ${className}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">The CPDForge Trust Stack</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {layers.map((l) => (
          <Link key={l.name} to={l.to} className="group flex items-start gap-2.5" data-testid={`footer-trust-${l.name.toLowerCase().replace(/\s+/g, '-')}`}>
            {l.orange
              ? <img src={CC_ICON} alt="" className="w-6 h-6 object-contain flex-shrink-0 mt-0.5" />
              : <ShieldCheck className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />}
            <span>
              <span className="block text-sm font-bold text-slate-900 dark:text-white group-hover:underline" style={l.orange ? { color: CC_ORANGE } : undefined}>{l.name}</span>
              <span className="block text-[11px] text-slate-500 dark:text-slate-400 leading-snug">{l.role}</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
